/**
 * auth.utils provides functions which are the same for both participant and admin, to cut down on repeated code
 */

interface DecodedJWT {
   exp?: number;
   permissions?: string[];
   is_admin?: boolean;
   impersonator_email?: string;
   user_id: string;
   user_email: string;
}

const decodeJWT = (token: string): DecodedJWT => {
   const base64Url = token.split(".")[1];
   const base64 = base64Url.replace("-", "+").replace("_", "/");
   return JSON.parse(window.atob(base64));
};

const isTokenValid = (decodedToken: DecodedJWT): boolean => {
   if (!decodedToken?.exp) return false;
   const expiration = decodedToken.exp;
   const now = Date.now() / 1000;
   return now < expiration;
};

const getDecodedTokenIncExpired = (jwt: string | null): DecodedJWT | null => {
   if (!jwt) return null;
   const dt: DecodedJWT = decodeJWT(jwt);
   return dt;
};

const getTokenStatus = (jwt: string | null): "valid" | "expired" | null => {
   if (!jwt) {
      return null;
   } else {
      const dt: DecodedJWT = decodeJWT(jwt);
      if (isTokenValid(dt)) {
         return "valid";
      } else {
         return "expired";
      }
   }
};

/** gets the device cookie from the document cookie */
const getDeviceCookie = (name: string): string | null => {
   // extracting a cookie by name - https://stackoverflow.com/a/21125098
   const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
   if (match) return match[2];
   else return null;
};

function getUserEmail(jwt: string | null): string | null {
   const dt = getDecodedTokenIncExpired(jwt);
   if (dt) return dt.user_email;
   return null;
}

function getUserId(jwt: string | null): string | null {
   const dt = getDecodedTokenIncExpired(jwt);
   if (dt) return dt.user_id;
   return null;
}

/** Does the JWT have at least ONE OF / ALL of the permissions */
const hasPermission = (permissions: string | string[], everyPermNeeded: boolean, jwt: string | null): boolean => {
   const permissionArray = Array.isArray(permissions) ? permissions : [permissions];
   const dt = getDecodedTokenIncExpired(jwt);
   if (!dt?.permissions) return false;

   const tokenPerms = dt.permissions;
   const isSuperUser = tokenPerms.includes("perm.superuser");
   if (isSuperUser) return true;

   const hasOneOfPerms = permissionArray.some((p) => tokenPerms.includes(p));
   const hasAllPerms = permissionArray.every((p) => tokenPerms.includes(p));

   if (everyPermNeeded) return hasAllPerms;
   return hasOneOfPerms;
};

/* Does a user have a valid jwt token and device cookie? This will return false if the jwt has expired, although that may not mean the user's session has expired.
 ** However it does mean that a refreshTheToken request must be made to check, and if the token is not successfully refreshed it will be destroyed */
const isAuthTokenAndDCValid = (jwt: string | null, deviceCookieName: string): boolean => {
   const deviceCookie = getDeviceCookie(deviceCookieName);
   const dt: DecodedJWT | null = jwt ? decodeJWT(jwt) : null;
   const validToken: boolean = dt ? isTokenValid(dt) : false;

   // when logged out, the device cookie is set to "logged out" (with double quotes!)
   return !!deviceCookie && !deviceCookie.includes("logged out") && validToken;
};

export default {
   decodeJWT,
   isTokenValid,
   getDecodedTokenIncExpired,
   getTokenStatus,
   getDeviceCookie,
   getUserEmail,
   getUserId,
   hasPermission,
   isAuthTokenAndDCValid,
};
